import { cn } from '../../../lib/cn'

export interface DotsEdgesProps {
  className?: string
}

/**
 * DotsEdges - Dot pattern background visible on edges, fading to center
 *
 * Creates a repeating dot pattern that is visible around the edges
 * and fades out toward the center. Inverse of DotsCenter.
 * Uses theme variables for automatic light/dark mode support.
 *
 * Fixed fadeSize: 0.75
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <DotsEdges />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function DotsEdges({ className }: DotsEdgesProps = {}) {
  const fadeSize = 0.75
  const size = `${fadeSize * 100}%`
  // Smooth eased gradient stops based on fadeSize
  const stop1 = `${fadeSize * 100 * 0.45}%`
  const stop2 = `${fadeSize * 100 * 0.85}%`
  const stop3 = `${fadeSize * 100 * 1.15}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, transparent 0%, transparent ${stop1}, rgba(0,0,0,0.4) ${stop2}, rgba(0,0,0,0.8) ${stop3}, #000 100%)`

  return (
    <div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `radial-gradient(circle, var(--border) 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
    />
  )
}
